import React, { useEffect, useRef, useState } from "react";
import {
    Building2, CreditCard, Bell, Archive, ShieldCheck, Shield, KeyRound,
    Upload, MapPin, UserRound, Save, X, Loader2, ExternalLink, Copy,
} from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, apiBaseUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SecuritySettings from "@/pages/SecuritySettings";
import SessionsPanel from "@/components/SessionsPanel";
import Notifications from "@/pages/Notifications";
import Retention from "@/pages/Retention";
import SsoSettings from "@/pages/SsoSettings";
import { TokensTab as ApiTokensPanel } from "@/pages/ApiTokens";

// Mirrors Layout.jsx's ROLE_RANK — the same minRole gates the removed
// sidebar entries used (Retention: engineer+, SSO & SCIM / API Tokens:
// admin+) are reapplied here so moving them into tabs doesn't loosen access.
const ROLE_RANK = { viewer: 1, auditor: 2, engineer: 3, admin: 4, superadmin: 5 };

export default function PortalSettings() {
    const { user } = useAuth();
    const [tab, setTab] = useState("organization");
    const isAdmin = user?.role === "admin" || user?.role === "superadmin";
    const userRank = ROLE_RANK[user?.role] || 0;
    const canSeeRetention = userRank >= ROLE_RANK.engineer;
    const canSeeAdminTabs = userRank >= ROLE_RANK.admin;

    return (
        <div className="p-6 sm:p-9" data-testid="portal-settings-page">
            <div className="mb-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                    Account
                </div>
                <h1 className="mt-1.5 font-display text-3xl font-bold tracking-tight">Settings</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                    Manage your organization, billing, security, and integrations.
                </p>
            </div>

            <div className="mb-6 flex flex-wrap gap-1 border-b border-border" data-testid="portal-settings-tabs">
                {[
                    { key: "organization", label: "Organization", icon: Building2 },
                    { key: "billing_details", label: "Billing Details", icon: CreditCard },
                    { key: "notifications", label: "Notifications", icon: Bell },
                    ...(canSeeRetention ? [{ key: "retention", label: "Retention", icon: Archive }] : []),
                    ...(canSeeAdminTabs ? [{ key: "sso", label: "SSO & SCIM", icon: ShieldCheck }] : []),
                    { key: "security", label: "Security (2FA)", icon: Shield },
                    ...(canSeeAdminTabs ? [{ key: "api_tokens", label: "API Tokens", icon: KeyRound }] : []),
                ].map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        data-testid={`portal-settings-tab-${t.key}`}
                        className={[
                            "flex items-center gap-1.5 border-b-2 px-3.5 py-2.5 text-[13px] font-medium",
                            tab === t.key ? "border-brand font-semibold text-brand" : "border-transparent text-muted-foreground hover:text-foreground",
                        ].join(" ")}
                    >
                        <t.icon size={15} /> {t.label}
                    </button>
                ))}
            </div>

            {tab === "organization" && <OrganizationTab isAdmin={isAdmin} />}
            {tab === "billing_details" && <BillingDetailsTab isAdmin={isAdmin} />}
            {tab === "notifications" && <Notifications />}
            {tab === "retention" && canSeeRetention && <Retention fullWidth />}
            {tab === "sso" && canSeeAdminTabs && <SsoSettings fullWidth />}
            {tab === "security" && (
                <div className="space-y-4">
                    <SecuritySettings fullWidth />
                    <SettingsCard icon={ShieldCheck} title="Active sessions" description="Devices and browsers currently signed in to your account.">
                        <SessionsPanel />
                    </SettingsCard>
                </div>
            )}
            {tab === "api_tokens" && canSeeAdminTabs && <ApiTokensPanel />}
        </div>
    );
}

/** Shared card shell used across every Settings tab — icon-box header +
 * title + description, matching the redesign brief's one consistent
 * card system (§9–10). */
export function SettingsCard({ icon: Icon, title, description, children, className = "", right = null }) {
    return (
        <div className={`rounded-xl border border-border bg-surface p-5 shadow-[0_1px_2px_rgba(16,24,40,0.02)] ${className}`}>
            <div className="mb-4 flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    {Icon && (
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-[#EEF4FF] dark:bg-brand/10">
                            <Icon size={19} className="text-brand" />
                        </div>
                    )}
                    <div>
                        <h2 className="font-display text-[15px] font-bold">{title}</h2>
                        {description && <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{description}</p>}
                    </div>
                </div>
                {right}
            </div>
            {children}
        </div>
    );
}

export function SettingsField({ label, children, full = false }) {
    return (
        <div className={full ? "sm:col-span-2" : ""}>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">{label}</label>
            {children}
        </div>
    );
}

const inputCls = "w-full rounded-md border border-input bg-background px-3 py-2.5 text-xs text-foreground outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/10 disabled:bg-muted/40 disabled:text-muted-foreground";

export function SettingsInput(props) {
    return <input {...props} className={`${inputCls} ${props.className || ""}`} />;
}
export function SettingsSelect({ children, ...props }) {
    return <select {...props} className={`${inputCls} ${props.className || ""}`}>{children}</select>;
}

function SaveButton({ saving, onClick, testId }) {
    return (
        <button
            onClick={onClick}
            disabled={saving}
            data-testid={testId}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand px-4 py-2.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
        >
            <Save size={14} /> {saving ? "Saving…" : "Save changes"}
        </button>
    );
}

// ── Organization tab ─────────────────────────────────────────────────────

function OrganizationTab({ isAdmin }) {
    const [org, setOrg] = useState(null);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const refresh = async () => {
        try {
            const { data } = await http.get("/organization");
            setOrg(data);
            setForm({
                name: data.name || "",
                company_size: data.company_size || "",
                industry: data.industry || "",
                country: data.country || "",
                contact_name: data.contact_name || "",
                contact_email: data.contact_email || "",
                contact_phone: data.contact_phone || "",
                contact_website: data.contact_website || "",
                address: {
                    address_line1: data.address?.address_line1 || "",
                    address_line2: data.address?.address_line2 || "",
                    city: data.address?.city || "",
                    province: data.address?.province || "",
                    postal_code: data.address?.postal_code || "",
                },
            });
        } catch (e) {
            setErr(errMsg(e));
        }
    };

    useEffect(() => { refresh(); }, []);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await http.patch("/organization", {
                name: form.name, company_size: form.company_size, industry: form.industry, country: form.country,
                contact_name: form.contact_name, contact_email: form.contact_email,
                contact_phone: form.contact_phone, contact_website: form.contact_website,
                address: form.address,
            });
            toast.success("Organization updated");
            refresh();
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setSaving(false);
        }
    };

    const uploadLogo = async (file) => {
        if (!file) return;
        if (!["image/png", "image/jpeg", "image/svg+xml"].includes(file.type)) {
            toast.error("Logo must be a PNG, JPG, or SVG file.");
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            toast.error("Logo must be 2 MB or smaller.");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await http.post("/organization/logo", fd);
            toast.success("Logo updated");
            refresh();
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setUploading(false);
        }
    };

    const removeLogo = async () => {
        setUploading(true);
        try {
            await http.delete("/organization/logo");
            toast.success("Logo removed");
            refresh();
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setUploading(false);
        }
    };

    if (err) return <div className="text-sm text-[hsl(var(--destructive))]">{err}</div>;
    if (!form) {
        return (
            <div className="flex items-center justify-center p-12 text-muted-foreground">
                <Loader2 size={16} className="mr-2 animate-spin" /> Loading…
            </div>
        );
    }

    return (
        <form onSubmit={save} className="space-y-4" data-testid="portal-settings-organization-form">
            <SettingsCard icon={Building2} title="Organization information" description="Update your company details. This information may appear on invoices and in communications.">
                <div className="grid gap-4 sm:grid-cols-2">
                    <SettingsField label="Organization name">
                        <SettingsInput
                            disabled={!isAdmin}
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            data-testid="portal-org-name-input"
                        />
                    </SettingsField>
                    <SettingsField label="Company size">
                        <SettingsSelect disabled={!isAdmin} value={form.company_size} onChange={(e) => setForm({ ...form, company_size: e.target.value })} data-testid="portal-org-size-input">
                            <option value="">—</option>
                            {["1-10", "11-50", "51-200", "201-500", "501-1000", "1001+"].map((v) => (
                                <option key={v} value={v}>{v.replace("-", "–")} employees</option>
                            ))}
                        </SettingsSelect>
                    </SettingsField>
                    <SettingsField label="Industry">
                        <SettingsInput
                            disabled={!isAdmin}
                            value={form.industry}
                            onChange={(e) => setForm({ ...form, industry: e.target.value })}
                            data-testid="portal-org-industry-input"
                        />
                    </SettingsField>
                    <SettingsField label="Country">
                        <SettingsInput
                            disabled={!isAdmin}
                            value={form.country}
                            onChange={(e) => setForm({ ...form, country: e.target.value })}
                            data-testid="portal-org-country-input"
                        />
                    </SettingsField>
                </div>

                {org?.slug && (
                    <div className="mt-4">
                        <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Tenant slug</label>
                        <div className="flex items-center gap-2 rounded-md border border-input bg-muted/20 px-3 py-2.5">
                            <code className="flex-1 font-mono text-xs text-foreground">{org.slug}</code>
                            <button
                                type="button"
                                onClick={() => { navigator.clipboard.writeText(org.slug); toast.success("Copied"); }}
                                aria-label="Copy tenant slug"
                                data-testid="portal-org-slug-copy"
                                className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                            >
                                <Copy size={13} />
                            </button>
                        </div>
                        <p className="mt-1 text-[11px] text-muted-foreground">Your unique organization identifier — used in SSO/SCIM URLs and API integrations.</p>
                    </div>
                )}

                {isAdmin && (
                    <div className="mt-5 grid gap-4 sm:grid-cols-[1fr_auto]">
                        <div className="flex items-center gap-3 rounded-lg border border-dashed border-border p-4">
                            <Upload size={18} className="shrink-0 text-brand" />
                            <div className="flex-1">
                                <div className="text-xs font-medium">Organization logo</div>
                                <div className="text-[11px] text-muted-foreground">PNG, JPG or SVG (max 2 MB)</div>
                            </div>
                            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
                            <button
                                type="button"
                                onClick={() => fileRef.current?.click()}
                                disabled={uploading}
                                data-testid="portal-org-logo-upload-btn"
                                aria-label="Upload organization logo"
                                className="rounded-md border border-input bg-background px-3 py-2 text-xs font-semibold hover:bg-muted/40 disabled:opacity-50"
                            >
                                {uploading ? "Uploading…" : "Choose file"}
                            </button>
                        </div>
                        {org?.logo_url && (
                            <div className="flex items-center gap-2 rounded-lg border border-border px-3">
                                <img src={`${apiBaseUrl()}${org.logo_url}`} alt="Current organization logo" className="max-h-[44px] max-w-[200px] object-contain" />
                                <button type="button" onClick={removeLogo} aria-label="Remove organization logo" className="text-muted-foreground hover:text-foreground">
                                    <X size={14} />
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </SettingsCard>

            <div className="grid gap-4 lg:grid-cols-2">
                <SettingsCard icon={UserRound} title="Contact information" description="Primary contact details for your organization.">
                    <div className="grid gap-4 sm:grid-cols-2">
                        <SettingsField label="Contact name">
                            <SettingsInput disabled={!isAdmin} value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="portal-org-contact-name-input" />
                        </SettingsField>
                        <SettingsField label="Email address">
                            <SettingsInput type="email" disabled={!isAdmin} value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="portal-org-contact-email-input" />
                        </SettingsField>
                        <SettingsField label="Phone number">
                            <SettingsInput disabled={!isAdmin} value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="portal-org-contact-phone-input" />
                        </SettingsField>
                        <SettingsField label="Website (optional)">
                            <SettingsInput placeholder="https://example.com" disabled={!isAdmin} value={form.contact_website} onChange={(e) => setForm({ ...form, contact_website: e.target.value })} data-testid="portal-org-contact-website-input" />
                        </SettingsField>
                    </div>
                </SettingsCard>

                <SettingsCard icon={MapPin} title="Organization address" description="This address may appear on invoices and official documents.">
                    <div className="grid gap-4 sm:grid-cols-2">
                        <SettingsField label="Address line 1">
                            <SettingsInput disabled={!isAdmin} value={form.address.address_line1} onChange={(e) => setForm({ ...form, address: { ...form.address, address_line1: e.target.value } })} data-testid="portal-org-address1-input" />
                        </SettingsField>
                        <SettingsField label="Address line 2 (optional)">
                            <SettingsInput disabled={!isAdmin} value={form.address.address_line2} onChange={(e) => setForm({ ...form, address: { ...form.address, address_line2: e.target.value } })} data-testid="portal-org-address2-input" />
                        </SettingsField>
                        <SettingsField label="City">
                            <SettingsInput disabled={!isAdmin} value={form.address.city} onChange={(e) => setForm({ ...form, address: { ...form.address, city: e.target.value } })} data-testid="portal-org-city-input" />
                        </SettingsField>
                        <SettingsField label="Province / State">
                            <SettingsInput disabled={!isAdmin} value={form.address.province} onChange={(e) => setForm({ ...form, address: { ...form.address, province: e.target.value } })} data-testid="portal-org-province-input" />
                        </SettingsField>
                        <SettingsField label="Postal code">
                            <SettingsInput disabled={!isAdmin} value={form.address.postal_code} onChange={(e) => setForm({ ...form, address: { ...form.address, postal_code: e.target.value } })} data-testid="portal-org-postal-input" />
                        </SettingsField>
                    </div>
                </SettingsCard>
            </div>

            {!isAdmin && (
                <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                    Only organization admins can edit these fields.
                </div>
            )}
            {isAdmin && <SaveButton saving={saving} testId="portal-settings-organization-save-btn" />}
        </form>
    );
}

// ── Billing Details tab ──────────────────────────────────────────────────

function BillingDetailsTab({ isAdmin }) {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const [sub, setSub] = useState(null);

    const refresh = async () => {
        try {
            const { data } = await http.get("/organization");
            setForm({
                billing_contact_name: data.billing?.contact_name || "",
                billing_email: data.billing?.email || "",
                billing_phone: data.billing?.phone || "",
                address_line1: data.billing?.address_line1 || "",
                address_line2: data.billing?.address_line2 || "",
                city: data.billing?.city || "",
                province: data.billing?.province || "",
                postal_code: data.billing?.postal_code || "",
                country: data.billing?.country || data.country || "",
            });
        } catch (e) {
            setErr(errMsg(e));
        }
    };
    useEffect(() => {
        refresh();
        http.get("/billing/subscription").then(({ data }) => setSub(data)).catch(() => {});
    }, []);

    const openPortal = async () => {
        try {
            const { data } = await http.post("/billing/portal", { return_url: window.location.href });
            window.location.href = data.url;
        } catch (e) {
            toast.error(errMsg(e));
        }
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await http.patch("/organization", {
                billing: {
                    contact_name: form.billing_contact_name, email: form.billing_email, phone: form.billing_phone,
                    address_line1: form.address_line1, address_line2: form.address_line2,
                    city: form.city, province: form.province, postal_code: form.postal_code, country: form.country,
                },
            });
            toast.success("Billing details updated");
            refresh();
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setSaving(false);
        }
    };

    if (err) return <div className="text-sm text-[hsl(var(--destructive))]">{err}</div>;
    if (!form) {
        return (
            <div className="flex items-center justify-center p-12 text-muted-foreground">
                <Loader2 size={16} className="mr-2 animate-spin" /> Loading…
            </div>
        );
    }

    const pm = sub?.payment_method;

    return (
        <form onSubmit={save} className="space-y-4" data-testid="portal-settings-billing-details-form">
            <SettingsCard icon={CreditCard} title="Billing information" description="Manage your billing contact information and default payment details.">
                <div className="grid gap-4 sm:grid-cols-3">
                    <SettingsField label="Billing contact name">
                        <SettingsInput disabled={!isAdmin} value={form.billing_contact_name} onChange={(e) => setForm({ ...form, billing_contact_name: e.target.value })} data-testid="portal-billing-contact-name-input" />
                    </SettingsField>
                    <SettingsField label="Billing email">
                        <SettingsInput type="email" disabled={!isAdmin} value={form.billing_email} onChange={(e) => setForm({ ...form, billing_email: e.target.value })} data-testid="portal-billing-email-input" />
                    </SettingsField>
                    <SettingsField label="Billing phone">
                        <SettingsInput disabled={!isAdmin} value={form.billing_phone} onChange={(e) => setForm({ ...form, billing_phone: e.target.value })} data-testid="portal-billing-phone-input" />
                    </SettingsField>
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <SettingsField label="Address line 1">
                        <SettingsInput disabled={!isAdmin} value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} data-testid="portal-billing-address1-input" />
                    </SettingsField>
                    <SettingsField label="Address line 2 (optional)">
                        <SettingsInput disabled={!isAdmin} value={form.address_line2} onChange={(e) => setForm({ ...form, address_line2: e.target.value })} data-testid="portal-billing-address2-input" />
                    </SettingsField>
                    <SettingsField label="City">
                        <SettingsInput disabled={!isAdmin} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="portal-billing-city-input" />
                    </SettingsField>
                    <SettingsField label="Province / State">
                        <SettingsInput disabled={!isAdmin} value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} data-testid="portal-billing-province-input" />
                    </SettingsField>
                    <SettingsField label="Postal code">
                        <SettingsInput disabled={!isAdmin} value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} data-testid="portal-billing-postal-input" />
                    </SettingsField>
                    <SettingsField label="Country">
                        <SettingsInput disabled={!isAdmin} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="portal-billing-country-input" />
                    </SettingsField>
                </div>
                {isAdmin && <div className="mt-5"><SaveButton saving={saving} testId="portal-settings-billing-details-save-btn" /></div>}
            </SettingsCard>

            <SettingsCard icon={CreditCard} title="Payment method" description="Your payment method is managed securely through Stripe.">
                {!sub ? (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 size={14} className="animate-spin" /> Loading…</div>
                ) : sub.managed_externally ? (
                    <div className="rounded-md border border-border bg-muted/30 px-3 py-2.5 text-xs text-muted-foreground">
                        This account is on a managed contract — payment isn't handled through Stripe self-serve.
                    </div>
                ) : pm ? (
                    <div className="flex items-center justify-between rounded-lg border border-border p-3">
                        <div className="flex items-center gap-3">
                            <div className="flex h-9 w-12 items-center justify-center rounded bg-[#EEF4FF] text-[10px] font-bold uppercase text-brand dark:bg-brand/10">{pm.brand}</div>
                            <div>
                                <div className="text-xs font-semibold">{pm.brand?.toUpperCase()} •••• {pm.last4}</div>
                                <div className="text-[11px] text-muted-foreground">Expires {pm.exp_month}/{pm.exp_year}</div>
                            </div>
                        </div>
                        {isAdmin && (
                            <button onClick={openPortal} type="button" aria-label="Manage payment method in Stripe" className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-xs font-semibold text-white hover:opacity-90">
                                <CreditCard size={13} /> Manage in Stripe <ExternalLink size={11} />
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="flex items-center justify-between rounded-md border border-dashed border-border px-3 py-2.5 text-xs text-muted-foreground">
                        No payment method on file.
                        {isAdmin && (
                            <button onClick={openPortal} type="button" aria-label="Manage payment method in Stripe" className="inline-flex items-center gap-1.5 text-brand hover:underline">
                                Manage in Stripe <ExternalLink size={11} />
                            </button>
                        )}
                    </div>
                )}
            </SettingsCard>
        </form>
    );
}
